import { BlankLayout, type ShellRouteHandle } from '@realtyfeed/app-shell';
import { PrivateRoute, PublicRoute } from '@realtyfeed/auth';
import { ROUTES } from '@realtyfeed/routes';
import { ErrorPage } from '@realtyfeed/ui';
import { lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { AppLayout } from '../components/AppLayout';
import { HomeRedirect } from '../components/HomeRedirect';
import { LazyRoute } from '../components/LazyRoute';

/**
 * The route table.
 *
 * Ported from the legacy `src/configs/router/index.tsx` — its **structure**, not its 2,783
 * lines. Three things changed on the way, all deliberate:
 *
 * 1. **The guard is on the branch, not the route.** The legacy carried `auth: true` on each of
 *    its 161 route objects and a `MergeLayoutRoutes` helper that re-derived the layout per
 *    route at render time. Here there are two branches — one behind `PublicRoute`, one behind
 *    `PrivateRoute` — and a route's position says what it needs. A route cannot be added to the
 *    private branch and accidentally left unguarded.
 * 2. **Breadcrumbs are route `handle` data**, read with `useMatches()`, rather than a
 *    `breadCrumb` field the router code interprets. Same information, and React Router already
 *    owns the plumbing.
 * 3. **Features are lazy.** Each screen is a `React.lazy` import behind its own `LazyRoute`
 *    boundary, so the initial bundle carries the shell and nothing else.
 *
 * Screens land here as their features are ported (`docs/03-task-backlog.md`). A route with no
 * feature yet is simply absent — there are no placeholder screens, because a page that renders
 * "coming soon" is indistinguishable from a broken one to the person looking at it.
 */

/** Route metadata, read by the shell's `RouteHeader` with `useMatches()`. */
export type RouteHandle = ShellRouteHandle;

const WhitelistIpScreen = lazy(() =>
  import('@/features/whitelist-ip').then((m) => ({ default: m.WhitelistIpScreen }))
);

const BlockedIpScreen = lazy(() =>
  import('@/features/blocked-ip').then((m) => ({ default: m.BlockedIpScreen }))
);

const SignInScreen = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.SignInScreen }))
);

const ForgotPasswordScreen = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.ForgotPasswordScreen }))
);

const ProfileScreen = lazy(() =>
  import('@/features/profile').then((m) => ({ default: m.ProfileScreen }))
);

const CustomersScreen = lazy(() =>
  import('@/features/customers').then((m) => ({ default: m.CustomersScreen }))
);

const MlsCustomersScreen = lazy(() =>
  import('@/features/customers').then((m) => ({ default: m.MlsCustomersScreen }))
);

const CustomerDetailScreen = lazy(() =>
  import('@/features/customers').then((m) => ({ default: m.CustomerDetailScreen }))
);

const MlsCustomerDetailScreen = lazy(() =>
  import('@/features/customers').then((m) => ({ default: m.MlsCustomerDetailScreen }))
);

const PackageInvoiceScreen = lazy(() =>
  import('@/features/customers').then((m) => ({ default: m.PackageInvoiceScreen }))
);

const PaymentDetailScreen = lazy(() =>
  import('@/features/customers').then((m) => ({ default: m.PaymentDetailScreen }))
);

const PaymentHistoryScreen = lazy(() =>
  import('@/features/customers').then((m) => ({ default: m.PaymentHistoryScreen }))
);

const UserLimitationScreen = lazy(() =>
  import('@/features/user-limitation').then((m) => ({ default: m.UserLimitationScreen }))
);

const ChangePlanScreen = lazy(() =>
  import('@/features/change-plan').then((m) => ({ default: m.ChangePlanScreen }))
);

const CustomerAnalyticsScreen = lazy(() =>
  import('@/features/customer-analytics').then((m) => ({
    default: m.CustomerAnalyticsScreen,
  }))
);

const MlsRequestEditScreen = lazy(() =>
  import('@/features/mls-requests').then((m) => ({ default: m.MlsRequestEditScreen }))
);
const MlsRequestDetailScreen = lazy(() =>
  import('@/features/mls-requests').then((m) => ({ default: m.MlsRequestDetailScreen }))
);

const MlsRequestsScreen = lazy(() =>
  import('@/features/mls-requests').then((m) => ({ default: m.MlsRequestsScreen }))
);

const ApiKeysScreen = lazy(() =>
  import('@/features/api-keys').then((m) => ({ default: m.ApiKeysScreen }))
);

const ApiKeyDetailScreen = lazy(() =>
  import('@/features/api-keys').then((m) => ({ default: m.ApiKeyDetailScreen }))
);

const ApiKeyConfigScreen = lazy(() =>
  import('@/features/api-keys').then((m) => ({ default: m.ApiKeyConfigScreen }))
);

const UsageLogScreen = lazy(() =>
  import('@/features/usage-log').then((m) => ({ default: m.UsageLogScreen }))
);

const MlsProvidersScreen = lazy(() =>
  import('@/features/mls-providers').then((m) => ({ default: m.MlsProvidersScreen }))
);

const MlsLogsScreen = lazy(() =>
  import('@/features/mls-providers').then((m) => ({ default: m.MlsLogsScreen }))
);

const MlsProviderDetailScreen = lazy(() =>
  import('@/features/mls-providers').then((m) => ({ default: m.MlsProviderDetailScreen }))
);

const MlsAccessDetailScreen = lazy(() =>
  import('@/features/mls-providers').then((m) => ({ default: m.MlsAccessDetailScreen }))
);

const MlsSalesScreen = lazy(() =>
  import('@/features/mls-providers').then((m) => ({ default: m.MlsSalesScreen }))
);

const MlsWizardScreen = lazy(() =>
  import('@/features/mls-providers').then((m) => ({ default: m.MlsWizardScreen }))
);

const MlsLimitationScreen = lazy(() =>
  import('@/features/mls-providers').then((m) => ({ default: m.MlsLimitationScreen }))
);

const PlatformsScreen = lazy(() =>
  import('@/features/mls-platform').then((m) => ({ default: m.PlatformsScreen }))
);

const PlatformDetailScreen = lazy(() =>
  import('@/features/mls-platform').then((m) => ({ default: m.PlatformDetailScreen }))
);

const PlatformWizardScreen = lazy(() =>
  import('@/features/mls-platform').then((m) => ({ default: m.PlatformWizardScreen }))
);

const MlsServerInvoiceScreen = lazy(() =>
  import('@/features/mls-server-invoice').then((m) => ({ default: m.MlsServerInvoiceScreen }))
);

const MlsPaymentForecastScreen = lazy(() =>
  import('@/features/mls-providers').then((m) => ({ default: m.MlsPaymentForecastScreen }))
);

const BillingAdjustmentScreen = lazy(() =>
  import('@/features/billing-adjustment').then((m) => ({ default: m.BillingAdjustmentScreen }))
);

const RolesScreen = lazy(() =>
  import('@/features/roles').then((m) => ({ default: m.RolesScreen }))
);

const RoleFormScreen = lazy(() =>
  import('@/features/roles').then((m) => ({ default: m.RoleFormScreen }))
);

const PermissionsScreen = lazy(() =>
  import('@/features/permissions').then((m) => ({ default: m.PermissionsScreen }))
);

const PermissionFormScreen = lazy(() =>
  import('@/features/permissions').then((m) => ({ default: m.PermissionFormScreen }))
);

const GroupsScreen = lazy(() =>
  import('@/features/groups').then((m) => ({ default: m.GroupsScreen }))
);

const GroupDetailScreen = lazy(() =>
  import('@/features/groups').then((m) => ({ default: m.GroupDetailScreen }))
);

const GroupWizardScreen = lazy(() =>
  import('@/features/groups').then((m) => ({ default: m.GroupWizardScreen }))
);

const GroupJoinRequestsScreen = lazy(() =>
  import('@/features/groups').then((m) => ({ default: m.GroupJoinRequestsScreen }))
);

const GroupMembersScreen = lazy(() =>
  import('@/features/groups').then((m) => ({ default: m.GroupMembersScreen }))
);

const GroupWhitelistScreen = lazy(() =>
  import('@/features/groups').then((m) => ({ default: m.GroupWhitelistScreen }))
);

const InviteMembersScreen = lazy(() =>
  import('@/features/groups').then((m) => ({ default: m.InviteMembersScreen }))
);

const NotificationVariablesScreen = lazy(() =>
  import('@/features/notifications').then((m) => ({ default: m.NotificationVariablesScreen }))
);

const NotificationListScreen = lazy(() =>
  import('@/features/notifications').then((m) => ({ default: m.NotificationListScreen }))
);

const NotificationFormScreen = lazy(() =>
  import('@/features/notifications').then((m) => ({ default: m.NotificationFormScreen }))
);

const NotificationContentScreen = lazy(() =>
  import('@/features/notifications').then((m) => ({ default: m.NotificationContentScreen }))
);

const NotificationSettingListScreen = lazy(() =>
  import('@/features/notifications').then((m) => ({ default: m.NotificationSettingListScreen }))
);

const MicroserviceTemplateListScreen = lazy(() =>
  import('@/features/notifications').then((m) => ({ default: m.MicroserviceTemplateListScreen }))
);

const MicroserviceTemplateFormScreen = lazy(() =>
  import('@/features/notifications').then((m) => ({ default: m.MicroserviceTemplateFormScreen }))
);

const ApplicationListScreen = lazy(() =>
  import('@/features/notifications').then((m) => ({ default: m.ApplicationListScreen }))
);

const ApplicationFormScreen = lazy(() =>
  import('@/features/notifications').then((m) => ({ default: m.ApplicationFormScreen }))
);

const GdxPartnerListScreen = lazy(() =>
  import('@/features/gdx-partner').then((m) => ({ default: m.GdxPartnerListScreen }))
);

const GdxPartnerFormScreen = lazy(() =>
  import('@/features/gdx-partner').then((m) => ({ default: m.GdxPartnerFormScreen }))
);

const GdxPartnerDetailScreen = lazy(() =>
  import('@/features/gdx-partner').then((m) => ({ default: m.GdxPartnerDetailScreen }))
);

const GdxPartnerSyncScreen = lazy(() =>
  import('@/features/gdx-partner').then((m) => ({ default: m.GdxPartnerSyncScreen }))
);

const AgreementTemplatesScreen = lazy(() =>
  import('@/features/agreements').then((m) => ({ default: m.AgreementTemplatesScreen }))
);

const AgreementTemplateFormScreen = lazy(() =>
  import('@/features/agreements').then((m) => ({ default: m.AgreementTemplateFormScreen }))
);

const SignedAgreementsScreen = lazy(() =>
  import('@/features/agreements').then((m) => ({ default: m.SignedAgreementsScreen }))
);

const MultiLanguageScreen = lazy(() =>
  import('@/features/multi-language').then((m) => ({ default: m.MultiLanguageScreen }))
);

const LanguageEditorScreen = lazy(() =>
  import('@/features/multi-language').then((m) => ({ default: m.LanguageEditorScreen }))
);

const GroupPaymentPlanScreen = lazy(() =>
  import('@/features/group-payment-plan').then((m) => ({ default: m.GroupPaymentPlanScreen }))
);

const GroupPlanSetInfoScreen = lazy(() =>
  import('@/features/group-payment-plan').then((m) => ({ default: m.GroupPlanSetInfoScreen }))
);

const AccountDeletionScreen = lazy(() =>
  import('@/features/account-deletion').then((m) => ({ default: m.AccountDeletionScreen }))
);

const FilterTemplatesScreen = lazy(() =>
  import('@/features/filter-templates').then((m) => ({ default: m.FilterTemplatesScreen }))
);

const TemplateFormScreen = lazy(() =>
  import('@/features/filter-templates').then((m) => ({ default: m.TemplateFormScreen }))
);

const UsersScreen = lazy(() =>
  import('@/features/users').then((m) => ({ default: m.UsersScreen }))
);

const UserDetailScreen = lazy(() =>
  import('@/features/users').then((m) => ({ default: m.UserDetailScreen }))
);

const StaffScreen = lazy(() =>
  import('@/features/staff').then((m) => ({ default: m.StaffScreen }))
);

const InviteStaffScreen = lazy(() =>
  import('@/features/staff').then((m) => ({ default: m.InviteStaffScreen }))
);

const StaffDetailScreen = lazy(() =>
  import('@/features/staff').then((m) => ({ default: m.StaffDetailScreen }))
);

const BttScreen = lazy(() => import('@/features/btt').then((m) => ({ default: m.BttScreen })));

const BttFormScreen = lazy(() =>
  import('@/features/btt').then((m) => ({ default: m.BttFormScreen }))
);

const BttDetailScreen = lazy(() =>
  import('@/features/btt').then((m) => ({ default: m.BttDetailScreen }))
);

const ResourcesScreen = lazy(() =>
  import('@/features/resources').then((m) => ({ default: m.ResourcesScreen }))
);

const ResourceFormScreen = lazy(() =>
  import('@/features/resources').then((m) => ({ default: m.ResourceFormScreen }))
);

const UsagePlansScreen = lazy(() =>
  import('@/features/usage-plans').then((m) => ({ default: m.UsagePlansScreen }))
);

const UsagePlanFormScreen = lazy(() =>
  import('@/features/usage-plans').then((m) => ({ default: m.UsagePlanFormScreen }))
);

const InvoiceManagementScreen = lazy(() =>
  import('@/features/invoice-management').then((m) => ({ default: m.InvoiceManagementScreen }))
);

const InvoiceDetailScreen = lazy(() =>
  import('@/features/invoice-management').then((m) => ({ default: m.InvoiceDetailScreen }))
);

const SubscriptionsScreen = lazy(() =>
  import('@/features/subscriptions').then((m) => ({ default: m.SubscriptionsScreen }))
);

const SubscriptionDetailScreen = lazy(() =>
  import('@/features/subscriptions').then((m) => ({ default: m.SubscriptionDetailScreen }))
);

const BillingAdjustmentAddScreen = lazy(() =>
  import('@/features/billing-adjustment').then((m) => ({ default: m.BillingAdjustmentAddScreen }))
);

const BillingAdjustmentDetailScreen = lazy(() =>
  import('@/features/billing-adjustment').then((m) => ({
    default: m.BillingAdjustmentDetailScreen,
  }))
);

const PackagesScreen = lazy(() =>
  import('@/features/packages').then((m) => ({ default: m.PackagesScreen }))
);

const PackageDetailScreen = lazy(() =>
  import('@/features/packages').then((m) => ({ default: m.PackageDetailScreen }))
);

const PackageWizardScreen = lazy(() =>
  import('@/features/packages').then((m) => ({ default: m.PackageWizardScreen }))
);

const MlsPaymentPlanScreen = lazy(() =>
  import('@/features/mls-payment-plan').then((m) => ({ default: m.MlsPaymentPlanScreen }))
);

const MlsPaymentPlanSetInfoScreen = lazy(() =>
  import('@/features/mls-payment-plan').then((m) => ({ default: m.MlsPaymentPlanSetInfoScreen }))
);

export const routes: RouteObject[] = [
  // ── Signed-out ────────────────────────────────────────────────────────────
  {
    element: (
      <PublicRoute redirectTo={ROUTES.home}>
        <BlankLayout />
      </PublicRoute>
    ),
    children: [
      {
        path: ROUTES.signIn,
        element: (
          <LazyRoute>
            <SignInScreen />
          </LazyRoute>
        ),
        handle: { title: 'Sign in' } satisfies RouteHandle,
      },
      {
        path: ROUTES.forgotPassword,
        element: (
          <LazyRoute>
            <ForgotPasswordScreen />
          </LazyRoute>
        ),
        handle: { title: 'Reset password' } satisfies RouteHandle,
      },
    ],
  },

  // ── Signed-in ─────────────────────────────────────────────────────────────
  {
    element: (
      <PrivateRoute redirectTo={ROUTES.signIn}>
        <AppLayout />
      </PrivateRoute>
    ),
    children: [
      {
        // No landing page exists; this picks the first screen the session can open.
        index: true,
        element: <HomeRedirect />,
      },
      {
        // The legacy kept `/dashboard` as a second door to the same redirect. Staff have it
        // bookmarked, so it stays rather than 404ing.
        path: ROUTES.dashboard,
        element: <HomeRedirect />,
      },
      {
        // `/profile` and `/profile/:tab` are the same screen; the segment selects the tab.
        path: ROUTES.profile,
        element: (
          <LazyRoute>
            <ProfileScreen />
          </LazyRoute>
        ),
        handle: { title: 'Profile' } satisfies RouteHandle,
      },
      {
        path: `${ROUTES.profile}/:tab`,
        element: (
          <LazyRoute>
            <ProfileScreen />
          </LazyRoute>
        ),
        handle: { title: 'Profile' } satisfies RouteHandle,
      },
      {
        /**
         * Registered but unreachable from the sidebar, which is how the legacy left it — the
         * route existed and no navigation entry pointed at it. Carried over rather than
         * promoted: this screen shows per-MLS revenue, cost and margin, and who sees those is
         * a product decision, not a side effect of a port. See D20.
         */
        path: `${ROUTES.customerAnalytics}/stats`,
        element: (
          <LazyRoute>
            <CustomerAnalyticsScreen />
          </LazyRoute>
        ),
        handle: { title: 'Customer analytics' } satisfies RouteHandle,
      },
      {
        path: ROUTES.customers,
        element: (
          <LazyRoute>
            <CustomersScreen />
          </LazyRoute>
        ),
        handle: { title: 'Customers' } satisfies RouteHandle,
      },
      {
        /**
         * The path segment is literally `Upgrade&Downgrade` — an ampersand in a route, which is
         * the legacy spelling and is kept because staff have it bookmarked. React Router
         * matches it as a literal; it is not a query separator here.
         */
        path: `${ROUTES.customers}/:id/:mls/Upgrade&Downgrade`,
        element: (
          <LazyRoute>
            <ChangePlanScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Change plan',
          hasBack: true,
          crumbs: [{ title: 'Customers', to: ROUTES.customers }],
        } satisfies RouteHandle,
      },
      {
        /**
         * The path segment is literally `Upgrade&Downgrade` — an ampersand in a route, which is
         * the legacy spelling and is kept because staff have it bookmarked. React Router
         * matches it as a literal; it is not a query separator here.
         */
        path: `${ROUTES.mlsCustomers}/:id/:mls/Upgrade&Downgrade`,
        element: (
          <LazyRoute>
            <ChangePlanScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Change plan',
          hasBack: true,
          crumbs: [{ title: 'MLS Customers', to: ROUTES.mlsCustomers }],
        } satisfies RouteHandle,
      },
      {
        path: `${ROUTES.mlsRequests}/edit/:id`,
        element: (
          <LazyRoute>
            <MlsRequestEditScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Edit request',
          hasBack: true,
          crumbs: [{ title: 'MLS Requests', to: ROUTES.mlsRequests }],
        } satisfies RouteHandle,
      },
      {
        path: `${ROUTES.mlsRequests}/:id`,
        element: (
          <LazyRoute>
            <MlsRequestDetailScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Request details',
          hasBack: true,
          crumbs: [{ title: 'MLS Requests', to: ROUTES.mlsRequests }],
        } satisfies RouteHandle,
      },
      {
        path: `${ROUTES.mlsRequests}/limitation/:user_id/:mls_id`,
        element: (
          <LazyRoute>
            <UserLimitationScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Feed limitation',
          hasBack: true,
          crumbs: [{ title: 'MLS Requests', to: ROUTES.mlsRequests }],
        } satisfies RouteHandle,
      },
      {
        path: `${ROUTES.mlsCustomers}/limitation/:user_id/:mls_id`,
        element: (
          <LazyRoute>
            <UserLimitationScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Feed limitation',
          hasBack: true,
          crumbs: [{ title: 'MLS Customers', to: ROUTES.mlsCustomers }],
        } satisfies RouteHandle,
      },
      {
        /**
         * Declared BEFORE the `/:id` detail route below. React Router ranks a static segment
         * above a dynamic one, so the order is not load-bearing — but reading it in URL
         * specificity order is how the next person checks that for themselves.
         */
        path: `${ROUTES.customers}/payment/:id/:customerName?`,
        element: (
          <LazyRoute>
            <PaymentHistoryScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Payment history',
          hasBack: true,
          crumbs: [{ title: 'Customers', to: ROUTES.customers }],
        } satisfies RouteHandle,
      },
      {
        path: `${ROUTES.mlsCustomers}/payment/:id/:customerName?`,
        element: (
          <LazyRoute>
            <PaymentHistoryScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Payment history',
          hasBack: true,
          crumbs: [{ title: 'MLS Customers', to: ROUTES.mlsCustomers }],
        } satisfies RouteHandle,
      },
      {
        path: `${ROUTES.customers}/payment/detail/:userId/:invoiceId`,
        element: (
          <LazyRoute>
            <PaymentDetailScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Invoice details',
          hasBack: true,
          crumbs: [{ title: 'Customers', to: ROUTES.customers }],
        } satisfies RouteHandle,
      },
      {
        path: `${ROUTES.mlsCustomers}/payment/detail/:userId/:invoiceId`,
        element: (
          <LazyRoute>
            <PaymentDetailScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Invoice details',
          hasBack: true,
          crumbs: [{ title: 'MLS Customers', to: ROUTES.mlsCustomers }],
        } satisfies RouteHandle,
      },
      {
        path: `${ROUTES.customers}/package-invoice/:id/:userId/:packageName?`,
        element: (
          <LazyRoute>
            <PackageInvoiceScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Payment history',
          hasBack: true,
          crumbs: [{ title: 'Customers', to: ROUTES.customers }],
        } satisfies RouteHandle,
      },
      {
        path: `${ROUTES.mlsCustomers}/package-invoice/:id/:userId/:packageName?`,
        element: (
          <LazyRoute>
            <PackageInvoiceScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Payment history',
          hasBack: true,
          crumbs: [{ title: 'MLS Customers', to: ROUTES.mlsCustomers }],
        } satisfies RouteHandle,
      },
      {
        path: `${ROUTES.customers}/:id`,
        element: (
          <LazyRoute>
            <CustomerDetailScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Customer',
          hasBack: true,
          crumbs: [{ title: 'Customers', to: ROUTES.customers }],
        } satisfies RouteHandle,
      },
      {
        path: `${ROUTES.mlsCustomers}/:id`,
        element: (
          <LazyRoute>
            <MlsCustomerDetailScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'MLS Customer',
          hasBack: true,
          crumbs: [{ title: 'MLS Customers', to: ROUTES.mlsCustomers }],
        } satisfies RouteHandle,
      },
      {
        /**
         * The legacy two-segment URL, kept so bookmarks of it still resolve.
         *
         * Its second segment was declared `:mls` but the list linked
         * `/mlsCustomer-list/${row.id}/${row.id}` — the SAME user id twice. So the segment
         * never carried an MLS id, the detail endpoint does not take one, and nothing read
         * it. New links use the one-segment form above; this renders the same screen.
         */
        path: `${ROUTES.mlsCustomers}/:id/:mls`,
        element: (
          <LazyRoute>
            <MlsCustomerDetailScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'MLS Customer',
          hasBack: true,
          crumbs: [{ title: 'MLS Customers', to: ROUTES.mlsCustomers }],
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.mlsCustomers,
        element: (
          <LazyRoute>
            <MlsCustomersScreen />
          </LazyRoute>
        ),
        handle: { title: 'MLS Customers' } satisfies RouteHandle,
      },
      {
        path: ROUTES.mlsRequests,
        element: (
          <LazyRoute>
            <MlsRequestsScreen />
          </LazyRoute>
        ),
        handle: { title: 'MLS Requests' } satisfies RouteHandle,
      },
      {
        // Two params: the usage plan and the client. See `UsageLogScreen` on why neither of
        // them is the API key's own id. Registered before `/api-mgmt/:id` for the same reason
        // the config route is.
        path: ROUTES.apiKeyUsageLog + '/:id/:userId',
        element: (
          <LazyRoute>
            <UsageLogScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Usage Log',
          hasBack: true,
          crumbs: [{ title: 'API Key Management', to: ROUTES.apiKeys }],
        } satisfies RouteHandle,
      },
      {
        // Before `/api-mgmt/:id`, which would otherwise match `/api-mgmt/config` with
        // `id === 'config'`.
        path: ROUTES.apiKeyConfig + '/:id',
        element: (
          <LazyRoute>
            <ApiKeyConfigScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'API Key Config',
          hasBack: true,
          crumbs: [{ title: 'API Key Management', to: ROUTES.apiKeys }],
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.apiKeys + '/:id',
        element: (
          <LazyRoute>
            <ApiKeyDetailScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'API Key',
          hasBack: true,
          crumbs: [{ title: 'API Key Management', to: ROUTES.apiKeys }],
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.apiKeys,
        element: (
          <LazyRoute>
            <ApiKeysScreen />
          </LazyRoute>
        ),
        handle: { title: 'API Key Management' } satisfies RouteHandle,
      },
      {
        path: ROUTES.mlsProviders + '/add-limitation/:mls_id',
        element: (
          <LazyRoute>
            <MlsLimitationScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Add MLS Filter',
          hasBack: true,
          crumbs: [{ title: 'MLS', to: ROUTES.mlsProviders }],
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.mlsProviders + '/edit-limitation/:mls_id/:id',
        element: (
          <LazyRoute>
            <MlsLimitationScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Edit MLS Filter',
          hasBack: true,
          crumbs: [{ title: 'MLS', to: ROUTES.mlsProviders }],
        } satisfies RouteHandle,
      },
      {
        // The three wizard doors, all ahead of `/mls-list/:id` so `add` and `edit` are not read
        // as ids. `/add/:id` is where a create lands once the record exists.
        path: ROUTES.mlsProviders + '/add',
        element: (
          <LazyRoute>
            <MlsWizardScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Add MLS',
          hasBack: true,
          crumbs: [{ title: 'MLS', to: ROUTES.mlsProviders }],
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.mlsProviders + '/add/:id',
        element: (
          <LazyRoute>
            <MlsWizardScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Add MLS',
          hasBack: true,
          crumbs: [{ title: 'MLS', to: ROUTES.mlsProviders }],
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.mlsProviders + '/edit/:id',
        element: (
          <LazyRoute>
            <MlsWizardScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Edit MLS',
          hasBack: true,
          crumbs: [{ title: 'MLS', to: ROUTES.mlsProviders }],
        } satisfies RouteHandle,
      },
      {
        // `/mls-list/access/:mlsId/:accessId`. Ahead of `/mls-list/:id` for the same reason the
        // logs route is — otherwise `access` matches as an id.
        path: ROUTES.mlsProviders + '/access/:mlsId/:accessId',
        element: (
          <LazyRoute>
            <MlsAccessDetailScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'MLS Access',
          hasBack: true,
          crumbs: [{ title: 'MLS', to: ROUTES.mlsProviders }],
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.mlsProviders + '/:id/sales',
        element: (
          <LazyRoute>
            <MlsSalesScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Sales & pricing',
          hasBack: true,
          crumbs: [{ title: 'MLS', to: ROUTES.mlsProviders }],
        } satisfies RouteHandle,
      },
      {
        // `/mls-list/logs/:id` — before `/mls-list/:id`, which would otherwise match `logs`.
        path: ROUTES.mlsLogs + '/:id',
        element: (
          <LazyRoute>
            <MlsLogsScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Log List',
          hasBack: true,
          crumbs: [{ title: 'MLS', to: ROUTES.mlsProviders }],
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.mlsProviders + '/:id',
        element: (
          <LazyRoute>
            <MlsProviderDetailScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'MLS Detail',
          hasBack: true,
          crumbs: [{ title: 'MLS', to: ROUTES.mlsProviders }],
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.mlsProviders,
        element: (
          <LazyRoute>
            <MlsProvidersScreen />
          </LazyRoute>
        ),
        handle: { title: 'MLS' } satisfies RouteHandle,
      },
      {
        // `add` and `edit` ahead of `/platforms/:id` so neither is read as an id.
        path: ROUTES.mlsPlatforms + '/add',
        element: (
          <LazyRoute>
            <PlatformWizardScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Add Platform',
          hasBack: true,
          crumbs: [{ title: 'Platforms', to: ROUTES.mlsPlatforms }],
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.mlsPlatforms + '/edit/:id/:step',
        element: (
          <LazyRoute>
            <PlatformWizardScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Edit Platform',
          hasBack: true,
          crumbs: [{ title: 'Platforms', to: ROUTES.mlsPlatforms }],
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.mlsPlatforms + '/:id',
        element: (
          <LazyRoute>
            <PlatformDetailScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Platform',
          hasBack: true,
          crumbs: [{ title: 'Platforms', to: ROUTES.mlsPlatforms }],
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.mlsPlatforms,
        element: (
          <LazyRoute>
            <PlatformsScreen />
          </LazyRoute>
        ),
        handle: { title: 'Platforms' } satisfies RouteHandle,
      },
      {
        path: ROUTES.mlsServerInvoice,
        element: (
          <LazyRoute>
            <MlsServerInvoiceScreen />
          </LazyRoute>
        ),
        handle: { title: 'MLS Server Invoice' } satisfies RouteHandle,
      },
      {
        path: ROUTES.mlsPaymentForecast,
        element: (
          <LazyRoute>
            <MlsPaymentForecastScreen />
          </LazyRoute>
        ),
        handle: { title: 'MLS Payment Forecast' } satisfies RouteHandle,
      },
      {
        path: ROUTES.packages,
        element: (
          <LazyRoute>
            <PackagesScreen />
          </LazyRoute>
        ),
        handle: { title: 'Packages' } satisfies RouteHandle,
      },
      {
        path: ROUTES.roles,
        element: (
          <LazyRoute>
            <RolesScreen />
          </LazyRoute>
        ),
        handle: { title: 'Roles' } satisfies RouteHandle,
      },
      {
        path: ROUTES.addRole,
        element: (
          <LazyRoute>
            <RoleFormScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Add Role',
          crumbs: [{ title: 'Roles', to: ROUTES.roles }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.editRole,
        element: (
          <LazyRoute>
            <RoleFormScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Edit Role',
          crumbs: [{ title: 'Roles', to: ROUTES.roles }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.permissions,
        element: (
          <LazyRoute>
            <PermissionsScreen />
          </LazyRoute>
        ),
        handle: { title: 'Permissions' } satisfies RouteHandle,
      },
      {
        path: ROUTES.addPermission,
        element: (
          <LazyRoute>
            <PermissionFormScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Add Permission',
          crumbs: [{ title: 'Permissions', to: ROUTES.permissions }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.groups,
        element: (
          <LazyRoute>
            <GroupsScreen />
          </LazyRoute>
        ),
        handle: { title: 'Groups' } satisfies RouteHandle,
      },
      {
        path: ROUTES.groupJoinRequests,
        element: (
          <LazyRoute>
            <GroupJoinRequestsScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Join Requests',
          crumbs: [{ title: 'Groups', to: ROUTES.groups }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.groupMembers,
        element: (
          <LazyRoute>
            <GroupMembersScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Group Members',
          crumbs: [{ title: 'Groups', to: ROUTES.groups }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.groupWhitelist,
        element: (
          <LazyRoute>
            <GroupWhitelistScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Group Whitelist',
          crumbs: [{ title: 'Groups', to: ROUTES.groups }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.inviteGroupMember,
        element: (
          <LazyRoute>
            <InviteMembersScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Invite Members',
          crumbs: [{ title: 'Groups', to: ROUTES.groups }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.addGroup,
        element: (
          <LazyRoute>
            <GroupWizardScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Create a Group',
          crumbs: [{ title: 'Groups', to: ROUTES.groups }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        // The handover route: a group was just created, and the BTT step needs its id.
        path: ROUTES.continueGroup,
        element: (
          <LazyRoute>
            <GroupWizardScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Create a Group',
          crumbs: [{ title: 'Groups', to: ROUTES.groups }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.editGroup,
        element: (
          <LazyRoute>
            <GroupWizardScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Edit Group',
          crumbs: [{ title: 'Groups', to: ROUTES.groups }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        // Last of the `/groups-list/*` routes, so the static segments are not swallowed by `:id`.
        path: ROUTES.groupDetail,
        element: (
          <LazyRoute>
            <GroupDetailScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Group Details',
          crumbs: [{ title: 'Groups', to: ROUTES.groups }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.notificationVariables,
        element: (
          <LazyRoute>
            <NotificationVariablesScreen />
          </LazyRoute>
        ),
        handle: { title: 'Notification Variables' } satisfies RouteHandle,
      },
      {
        path: ROUTES.addNotification,
        element: (
          <LazyRoute>
            <NotificationFormScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Add Notification',
          crumbs: [{ title: 'Notification List', to: ROUTES.notifications }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.editNotification,
        element: (
          <LazyRoute>
            <NotificationFormScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Edit Notification',
          crumbs: [{ title: 'Notification List', to: ROUTES.notifications }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.editNotificationFromSettings,
        element: (
          <LazyRoute>
            <NotificationFormScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Edit Notification',
          crumbs: [{ title: 'Notification Settings', to: ROUTES.notificationSettings }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.editMultiLanguage,
        element: (
          <LazyRoute>
            <LanguageEditorScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Edit Language',
          crumbs: [{ title: 'Language List', to: ROUTES.multiLanguage }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.multiLanguage,
        element: (
          <LazyRoute>
            <MultiLanguageScreen />
          </LazyRoute>
        ),
        handle: { title: 'Multi language list' } satisfies RouteHandle,
      },
      {
        path: ROUTES.addAgreementTemplate,
        element: (
          <LazyRoute>
            <AgreementTemplateFormScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Add New Agreement',
          crumbs: [{ title: 'Agreement Templates', to: ROUTES.agreementTemplates }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.editAgreementTemplate,
        element: (
          <LazyRoute>
            <AgreementTemplateFormScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Edit Agreement',
          crumbs: [{ title: 'Agreement Templates', to: ROUTES.agreementTemplates }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.agreementTemplates,
        element: (
          <LazyRoute>
            <AgreementTemplatesScreen />
          </LazyRoute>
        ),
        handle: { title: 'Agreement Templates' } satisfies RouteHandle,
      },
      {
        path: ROUTES.signedAgreements,
        element: (
          <LazyRoute>
            <SignedAgreementsScreen />
          </LazyRoute>
        ),
        handle: { title: 'Signed Agreements' } satisfies RouteHandle,
      },
      {
        path: ROUTES.addGdxPartner,
        element: (
          <LazyRoute>
            <GdxPartnerFormScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Add GDX Partner',
          crumbs: [{ title: 'GDX Partners', to: ROUTES.gdxPartner }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.editGdxPartner,
        element: (
          <LazyRoute>
            <GdxPartnerFormScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Edit GDX Partner',
          crumbs: [{ title: 'GDX Partners', to: ROUTES.gdxPartner }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.gdxPartnerDetail,
        element: (
          <LazyRoute>
            <GdxPartnerDetailScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'GDX Partner Details',
          crumbs: [{ title: 'GDX Partners', to: ROUTES.gdxPartner }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.syncGdxPartner,
        element: (
          <LazyRoute>
            <GdxPartnerSyncScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Sync GDX Partner',
          crumbs: [{ title: 'GDX Partners', to: ROUTES.gdxPartner }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.gdxPartner,
        element: (
          <LazyRoute>
            <GdxPartnerListScreen />
          </LazyRoute>
        ),
        handle: { title: 'GDX Partners' } satisfies RouteHandle,
      },
      {
        path: ROUTES.addNotificationApplication,
        element: (
          <LazyRoute>
            <ApplicationFormScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Add Application',
          crumbs: [{ title: 'Applications', to: ROUTES.notificationApplications }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.editNotificationApplication,
        element: (
          <LazyRoute>
            <ApplicationFormScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Edit Application',
          crumbs: [{ title: 'Applications', to: ROUTES.notificationApplications }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.notificationApplications,
        element: (
          <LazyRoute>
            <ApplicationListScreen />
          </LazyRoute>
        ),
        handle: { title: 'Applications' } satisfies RouteHandle,
      },
      {
        path: ROUTES.addMicroserviceTemplate,
        element: (
          <LazyRoute>
            <MicroserviceTemplateFormScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Add Microservice Template',
          crumbs: [{ title: 'Microservice Templates', to: ROUTES.microserviceTemplates }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.editMicroserviceTemplate,
        element: (
          <LazyRoute>
            <MicroserviceTemplateFormScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Edit Microservice Template',
          crumbs: [{ title: 'Microservice Templates', to: ROUTES.microserviceTemplates }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.microserviceTemplates,
        element: (
          <LazyRoute>
            <MicroserviceTemplateListScreen />
          </LazyRoute>
        ),
        handle: { title: 'Microservice Templates' } satisfies RouteHandle,
      },
      {
        path: ROUTES.notificationSettings,
        element: (
          <LazyRoute>
            <NotificationSettingListScreen />
          </LazyRoute>
        ),
        handle: { title: 'Notification Settings' } satisfies RouteHandle,
      },
      {
        path: ROUTES.notificationContent,
        element: (
          <LazyRoute>
            <NotificationContentScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Content and Language',
          crumbs: [{ title: 'Notification List', to: ROUTES.notifications }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.notifications,
        element: (
          <LazyRoute>
            <NotificationListScreen />
          </LazyRoute>
        ),
        handle: { title: 'Notification List' } satisfies RouteHandle,
      },
      {
        path: ROUTES.groupPaymentPlans,
        element: (
          <LazyRoute>
            <GroupPaymentPlanScreen />
          </LazyRoute>
        ),
        handle: { title: 'Group Payment Plans' } satisfies RouteHandle,
      },
      {
        path: ROUTES.groupPaymentPlanSetInfo,
        element: (
          <LazyRoute>
            <GroupPlanSetInfoScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Set Payment Info',
          crumbs: [{ title: 'Group payment plans', to: ROUTES.groupPaymentPlans }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.accountDeletion,
        element: (
          <LazyRoute>
            <AccountDeletionScreen />
          </LazyRoute>
        ),
        handle: { title: 'Account Deletion' } satisfies RouteHandle,
      },
      {
        path: ROUTES.filterTemplates,
        element: (
          <LazyRoute>
            <FilterTemplatesScreen />
          </LazyRoute>
        ),
        handle: { title: 'Filter Templates' } satisfies RouteHandle,
      },
      {
        path: ROUTES.addFilterTemplate,
        element: (
          <LazyRoute>
            <TemplateFormScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Add Filter Template',
          crumbs: [{ title: 'Filter Templates', to: ROUTES.filterTemplates }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.editFilterTemplate,
        element: (
          <LazyRoute>
            <TemplateFormScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Edit Filter Template',
          crumbs: [{ title: 'Filter Templates', to: ROUTES.filterTemplates }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.users,
        element: (
          <LazyRoute>
            <UsersScreen />
          </LazyRoute>
        ),
        handle: { title: 'Users' } satisfies RouteHandle,
      },
      {
        path: ROUTES.userDetail,
        element: (
          <LazyRoute>
            <UserDetailScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'User Information',
          crumbs: [{ title: 'Users', to: ROUTES.users }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.staff,
        element: (
          <LazyRoute>
            <StaffScreen />
          </LazyRoute>
        ),
        handle: { title: 'Staff' } satisfies RouteHandle,
      },
      {
        path: ROUTES.inviteStaff,
        element: (
          <LazyRoute>
            <InviteStaffScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Invite Staff',
          crumbs: [{ title: 'Staff', to: ROUTES.staff }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        // After `/staff/add`, so the static segment is not swallowed by `:id`.
        path: ROUTES.staffDetail,
        element: (
          <LazyRoute>
            <StaffDetailScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Staff Information',
          crumbs: [{ title: 'Staff', to: ROUTES.staff }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.btt,
        element: (
          <LazyRoute>
            <BttScreen />
          </LazyRoute>
        ),
        handle: { title: 'BTT List' } satisfies RouteHandle,
      },
      {
        path: ROUTES.addBtt,
        element: (
          <LazyRoute>
            <BttFormScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Add BTT',
          crumbs: [{ title: 'BTTs', to: ROUTES.btt }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        // The handover route: a BTT was just created, and steps 2–4 need its id.
        path: ROUTES.continueBtt,
        element: (
          <LazyRoute>
            <BttFormScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Add BTT',
          crumbs: [{ title: 'BTTs', to: ROUTES.btt }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.editBtt,
        element: (
          <LazyRoute>
            <BttFormScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Edit BTT',
          crumbs: [{ title: 'BTTs', to: ROUTES.btt }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        // Last of the four, so `/btt-list/add` and `/btt-list/edit/:id` are not swallowed by `:id`.
        path: ROUTES.bttDetail,
        element: (
          <LazyRoute>
            <BttDetailScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'BTT Details',
          crumbs: [{ title: 'BTTs', to: ROUTES.btt }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.resources,
        element: (
          <LazyRoute>
            <ResourcesScreen />
          </LazyRoute>
        ),
        handle: { title: 'Resources' } satisfies RouteHandle,
      },
      {
        path: ROUTES.addResource,
        element: (
          <LazyRoute>
            <ResourceFormScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Add Resource',
          crumbs: [{ title: 'Resources', to: ROUTES.resources }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.usagePlans,
        element: (
          <LazyRoute>
            <UsagePlansScreen />
          </LazyRoute>
        ),
        handle: { title: 'Usage plan list' } satisfies RouteHandle,
      },
      {
        path: ROUTES.addUsagePlan,
        element: (
          <LazyRoute>
            <UsagePlanFormScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Add Usage Plan',
          crumbs: [{ title: 'Usage Plans', to: ROUTES.usagePlans }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.editUsagePlan,
        element: (
          <LazyRoute>
            <UsagePlanFormScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Edit Usage Plan',
          crumbs: [{ title: 'Usage Plans', to: ROUTES.usagePlans }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.invoices,
        element: (
          <LazyRoute>
            <InvoiceManagementScreen />
          </LazyRoute>
        ),
        handle: { title: 'Invoice Management' } satisfies RouteHandle,
      },
      {
        path: ROUTES.invoiceDetail,
        element: (
          <LazyRoute>
            <InvoiceDetailScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Invoice Detail',
          crumbs: [{ title: 'Invoice Management', to: ROUTES.invoices }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.subscriptions,
        element: (
          <LazyRoute>
            <SubscriptionsScreen />
          </LazyRoute>
        ),
        handle: { title: 'Subscription' } satisfies RouteHandle,
      },
      {
        path: ROUTES.subscriptionDetail,
        element: (
          <LazyRoute>
            <SubscriptionDetailScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Subscription Detail',
          crumbs: [{ title: 'Subscriptions', to: ROUTES.subscriptions }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.billingAdjustment,
        element: (
          <LazyRoute>
            <BillingAdjustmentScreen />
          </LazyRoute>
        ),
        handle: { title: 'Billing Adjustment Panel' } satisfies RouteHandle,
      },
      {
        path: ROUTES.billingAdjustmentAdd,
        element: (
          <LazyRoute>
            <BillingAdjustmentAddScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Add Billing Adjustment',
          crumbs: [{ title: 'Billing', to: ROUTES.billingAdjustment }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.billingAdjustmentDetail,
        element: (
          <LazyRoute>
            <BillingAdjustmentDetailScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Details',
          crumbs: [{ title: 'Billing', to: ROUTES.billingAdjustment }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.packageAdd,
        element: (
          <LazyRoute>
            <PackageWizardScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Add Package',
          crumbs: [{ title: 'Packages', to: ROUTES.packages }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.packageAddWithId,
        element: (
          <LazyRoute>
            <PackageWizardScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Add Package',
          crumbs: [{ title: 'Packages', to: ROUTES.packages }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.packageEdit,
        element: (
          <LazyRoute>
            <PackageWizardScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Edit Package',
          crumbs: [{ title: 'Packages', to: ROUTES.packages }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.packageDetail,
        element: (
          <LazyRoute>
            <PackageDetailScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Package Detail',
          crumbs: [{ title: 'Packages', to: ROUTES.packages }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.mlsPaymentPlans,
        element: (
          <LazyRoute>
            <MlsPaymentPlanScreen />
          </LazyRoute>
        ),
        handle: { title: 'MLS Payment Plan List' } satisfies RouteHandle,
      },
      {
        path: ROUTES.mlsPaymentPlanSetInfo,
        element: (
          <LazyRoute>
            <MlsPaymentPlanSetInfoScreen />
          </LazyRoute>
        ),
        handle: {
          title: 'Set Info',
          crumbs: [{ title: 'MLS Payment Plans', to: ROUTES.mlsPaymentPlans }],
          hasBack: true,
        } satisfies RouteHandle,
      },
      {
        path: ROUTES.blockedIps,
        element: (
          <LazyRoute>
            <BlockedIpScreen />
          </LazyRoute>
        ),
        handle: { title: 'Blocked IP lists' } satisfies RouteHandle,
      },
      {
        path: ROUTES.whitelistIps,
        element: (
          <LazyRoute>
            <WhitelistIpScreen />
          </LazyRoute>
        ),
        handle: { title: 'Whitelist IP lists' } satisfies RouteHandle,
      },
    ],
  },

  // ── Chrome ────────────────────────────────────────────────────────────────
  {
    path: ROUTES.error,
    element: <BlankLayout />,
    children: [{ index: true, element: <ErrorPage /> }],
  },
  {
    // Anything unmatched. `/error` renders the same page, so this is a redirect rather than a
    // second copy — the legacy had both and they had already drifted apart.
    path: '*',
    element: <Navigate to={ROUTES.error} replace />,
  },
];
